import React, { useCallback, useMemo, memo, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import EditorDoneIcon from '@atlaskit/icon/glyph/editor/done';
import LinkIcon from '@atlaskit/icon/glyph/link';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Flex, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import {
	DEPENDENCY_DETAILS_ICON_NUDGE_STEP,
	useNudgeStep,
} from '@atlassian/jira-business-onboarding-timeline-dependencies';
import { DONE, statusCategoryForId } from '@atlassian/jira-common-constants/src/status-categories';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useIsLinkingEnabled } from '@atlassian/jira-router-resources-business-configuration';
import { type IssueId, toIssueId } from '@atlassian/jira-shared-types';
import { useViewport, useTimelineState } from '@atlassian/jira-software-roadmap-timeline-table';
import {
	DEPENDENCIES_FLYOUT_TYPE,
	CHART_BAR_CONTENT_Z_INDEX,
} from '@atlassian/jira-software-roadmap-timeline-table-kit';
import { useIssuesMap } from '../../../../../common/utils/use-issues-map';
import { useDependencyTooltip } from '../../../../../controllers/dependency-tooltip';
import type { TimelineViewIssue } from '../../../../../types';
import messages from './messages';

const ICON_SIZE = 20;
const BAR_PADDING = 12 * 2;

type BarIconContentProps = {
	dependees: IssueId[];
	dependencies: IssueId[];
	id: IssueId;
	isHovered: boolean;
	issue: TimelineViewIssue;
	withAvatar: boolean;
	withDoneIcon: boolean;
	withIconNudge: boolean;
	withLinksIcon: boolean;
};

export const BarIconContent = memo<BarIconContentProps>(
	({
		dependees,
		dependencies,
		id,
		isHovered,
		issue,
		withAvatar,
		withDoneIcon,
		withIconNudge,
		withLinksIcon,
	}: BarIconContentProps) => {
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const [, { setTooltipParams }] = useDependencyTooltip();

		const onLinkIconClick = useCallback(
			(event: MouseEvent<HTMLDivElement>) => {
				const target = event.currentTarget;
				const analyticsEvent = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'icon',
				});
				fireUIAnalytics(analyticsEvent, 'dependencyIcon');
				const links = {
					fromIds: dependencies,
					toIds: dependees,
				};

				// Roadmap's dependency tooltip relies on a render caused by "onClose" for correct positioning,
				// where it re-mounts inline-dialog. Therefore we let React unmount the dialog before setting new position
				setTimeout(() => {
					if (target != null) {
						const { top, left, height } = target.getBoundingClientRect();
						const position = { x: left, y: top + height };
						setTooltipParams({
							id,
							type: DEPENDENCIES_FLYOUT_TYPE,
							links,
							position,
						});
					}
				});
			},
			[createAnalyticsEvent, dependencies, dependees, setTooltipParams, id],
		);

		const detailsIconContent = (
			<LinkIconWrapper onClick={onLinkIconClick}>
				<Tooltip position="top" content={formatMessage(messages.viewDetail)} hideTooltipOnClick>
					<LinkIcon size="small" label="" primaryColor={token('color.icon.inverse', colors.N0)} />
				</Tooltip>
			</LinkIconWrapper>
		);
		const detailsIcon = withIconNudge ? (
			<SpotlightTarget name={DEPENDENCY_DETAILS_ICON_NUDGE_STEP}>
				{detailsIconContent}
			</SpotlightTarget>
		) : (
			detailsIconContent
		);

		return (
			<BarContentWrapper isHovered={isHovered} withAvatar={withAvatar}>
				{withAvatar && (
					<Avatar appearance="circle" src={issue?.assignee?.avatarUrl} size="xsmall" />
				)}
				{(withDoneIcon || withLinksIcon) && (
					<IconsWrapper>
						{withDoneIcon && (
							<EditorDoneIcon
								label={formatMessage(messages.doneLabel)}
								primaryColor={token('color.icon.inverse', colors.N0)}
								size="small"
							/>
						)}
						{withLinksIcon && detailsIcon}
					</IconsWrapper>
				)}
			</BarContentWrapper>
		);
	},
);

export type Props = {
	id: string;
	isHovered: boolean;
	leftPosition: number;
	rightPosition: number;
};

export const BarIcon = ({ id, isHovered, leftPosition, rightPosition }: Props) => {
	const [{ timelineWidth }] = useTimelineState();
	const [{ issuesMap }] = useIssuesMap();
	const [{ nudgeStep }] = useNudgeStep();
	const { requestViewportScrollBounding } = useViewport();
	const isLinkingEnabled = useIsLinkingEnabled();

	const issue = issuesMap.get(id);

	const [dependencies, dependees] = useMemo(() => {
		const [inwards, outwards] = [[], []];
		(issue?.issueLinks || []).forEach((link) => {
			const { inward, outward } = link;
			const inwardId = inward?.issueId;
			const outwardId = outward?.issueId;
			if (inwardId && inwardId !== issue?.issueId && issuesMap.has(inwardId)) {
				// @ts-expect-error - TS2345 - Argument of type 'any' is not assignable to parameter of type 'never'.
				inwards.push(inwardId);
			}
			if (outwardId && outwardId !== issue?.issueId && issuesMap.has(outwardId)) {
				// @ts-expect-error - TS2345 - Argument of type 'any' is not assignable to parameter of type 'never'.
				outwards.push(outwardId);
			}
		});

		return [inwards, outwards] as const;
	}, [issuesMap, issue]);

	const [firstItem] = Array.from(issuesMap.values());

	const isDone = useMemo(() => statusCategoryForId(issue?.status?.categoryId) === DONE, [issue]);
	const hasLinks = isLinkingEnabled && (dependencies.length > 0 || dependees.length > 0);

	if (!issue) {
		return null;
	}

	// Determine which icons should be shown based on the available width of the bar
	const barWidth = timelineWidth - leftPosition - rightPosition;
	let shouldShowDone = false;
	let shouldShowAvatar = false;
	let availableIconSpaces = (barWidth - BAR_PADDING) / ICON_SIZE;
	if (hasLinks) {
		availableIconSpaces -= 1;
	}
	if (isDone) {
		shouldShowDone = availableIconSpaces >= 1;
		availableIconSpaces -= 1;
	}
	if (availableIconSpaces >= 1) {
		shouldShowAvatar = true;
	}

	// Determine whether to render onboarding nudges
	const { scrollLeft, scrollRight } = requestViewportScrollBounding();
	const isBarInPast = leftPosition < scrollLeft;
	const isBarInFuture = rightPosition < scrollRight;
	const isBarInView = !isBarInPast && !isBarInFuture;

	const isFirstItem = id === firstItem?.issueId;
	const shouldRenderIconNudge =
		isFirstItem && nudgeStep === DEPENDENCY_DETAILS_ICON_NUDGE_STEP && isBarInView;

	const content = (
		<BarIconContent
			dependees={dependees}
			dependencies={dependencies}
			id={toIssueId(id)}
			isHovered={isHovered}
			issue={issue}
			withAvatar={shouldShowAvatar}
			withDoneIcon={shouldShowDone}
			withIconNudge={shouldRenderIconNudge}
			withLinksIcon={hasLinks}
		/>
	);

	return content;
};

// eslint-disable-next-line jira/styled/styled-component-order
const barContentWrapperBaseStyles = xcss({
	flex: 1,
	transition: 'padding-left 100ms ease-in-out',
	pointerEvents: 'none',
	marginRight: 'space.0',
	paddingLeft: 'space.0',
});

// eslint-disable-next-line jira/styled/styled-component-order
const barContentWrapperWithAvatarStyles = xcss({
	marginRight: 'space.100',
});

// eslint-disable-next-line jira/styled/styled-component-order
const barContentWrapperWithAvatarAndHoverStyles = xcss({
	paddingLeft: 'space.100',
});

const BarContentWrapper = ({
	isHovered,
	withAvatar,
	children,
}: {
	isHovered: boolean;
	withAvatar: boolean;
	children: React.ReactNode;
}) => (
	<Flex
		alignItems="center"
		justifyContent={withAvatar ? 'space-between' : 'center'}
		xcss={[
			barContentWrapperBaseStyles,
			withAvatar && barContentWrapperWithAvatarStyles,
			isHovered && withAvatar && barContentWrapperWithAvatarAndHoverStyles,
		]}
	>
		{children}
	</Flex>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkIconWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: `${ICON_SIZE}px`,
	height: `${ICON_SIZE}px`,
	borderRadius: '50%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: CHART_BAR_CONTENT_Z_INDEX,
	cursor: 'pointer',
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered', 'rgba(0, 0, 0, 0.1)'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		display: 'flex',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconsWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
});
